import {useMemo} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {useAutoUpdatePreview} from 'analysis-tool/data-frame-creation/hooks/useAutoUpdatePreview';
import {assertAndContinue} from 'Asserts';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  getPreviewOutputStepData,
  PreviewChart,
  PreviewOutput,
  PreviewStepParams,
} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {NOT_FETCHED_ASYNC_STATE, AsyncState, AsyncStatus} from 'ui-library/hooks/useAsyncState';

const previewAsyncGlobalState =
  makeUseGlobalClientState<AsyncState<PreviewOutput<PipelineStepType.CHART_VIEW>>>(NOT_FETCHED_ASYNC_STATE);

function generatePreview(
  data: PreviewStepParams<PipelineStepType.CHART_VIEW> | null,
  setAsyncState: (nextState: AsyncState<PreviewOutput<PipelineStepType.CHART_VIEW>>) => void
): void {
  if (data == null) {
    return;
  }
  AnalysisActions.runPreviewAnalysisStep(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
}

export type UsePreviewDraftStepOutput = {
  previewStatus: AsyncStatus;
  chartPreviews: Array<PreviewChart>;
  errorMessage?: string;
} | null;

/**
 * Provide a view for previewing a draft step.
 *
 * We can extend this to support previewing outputs other than charts in the views.
 */
export default function usePreviewDraftStep(
  stepToPreview: PipelineStep<PipelineStepType.CHART_VIEW, 0> | null,
  clientId: string
): UsePreviewDraftStepOutput {
  assertAndContinue(
    stepToPreview == null || stepToPreview.stepType === PipelineStepType.CHART_VIEW,
    'Expected stepToPreview to be null or a CHART_VIEW step'
  );
  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(clientId);

  const previewStepParams = useMemo(
    () =>
      stepToPreview != null && stepToPreview.config != null
        ? {
            stepType: PipelineStepType.CHART_VIEW,
            stepConfig: stepToPreview.config,
            stepInput:
              stepToPreview.inputStepDatas.length > 0
                ? {inputDataFrameId: stepToPreview.inputStepDatas[0].rawDatas[0].id}
                : {inputDataFrameId: []},
          }
        : null,
    [stepToPreview]
  );

  useAutoUpdatePreview(previewStepParams, generatePreview, setPreviewState);

  if (stepToPreview == null) {
    return null;
  }

  return {
    previewStatus: previewState.status,
    chartPreviews:
      previewState.status === AsyncStatus.SUCCESS
        ? getPreviewOutputStepData(previewState.data, 'Output chart', PipelineStepDataKind.CHART)
        : [],
    errorMessage: previewState.status === AsyncStatus.ERROR ? previewState.message : undefined,
  };
}
